// src/components/padrekko/qc/MeasurementSaveHUD.jsx
//
// PadRekko "save this measurement" HUD. Appears directly below the ephemeral
// ruler result pill, but ONLY inside a PadRekko case context and ONLY once a
// measurement is COMPLETE (measure.result set) — so it never interrupts an
// in-progress measurement. Lets the user attach the measurement to the case, a
// pad, or a finding, with an optional label, and persists it.
//
// The generic ruler/CommandRail is untouched; this is a separate context-gated
// overlay positioned to read as an extension of the pill.

import { useState } from "react";
import { Loader2, Save, X as XIcon } from "lucide-react";
import { useQcStore } from "../../../stores/useQcStore";

const ACRE_M2 = 4046.8564224;

function fmtDistance(m) {
  if (m >= 1000) return `${(m / 1000).toFixed(2)} km`;
  return `${m < 10 ? m.toFixed(1) : Math.round(m)} m`;
}
function fmtArea(sqm) {
  const ac = sqm / ACRE_M2;
  return `${ac < 1 ? ac.toFixed(3) : ac.toFixed(2)} acres`;
}

// Translate the ruler result into the persisted measurement fields + GeoJSON.
// (Arrows are drawn via the Annotations tool, not here.)
function toPayload(result) {
  if (result.kind === "line") {
    return {
      annotation_type: "distance",
      geometry: { type: "LineString", coordinates: result.coords },
      measurement_value: fmtDistance(result.distance),
      measurement_unit: "meters",
      measurement_raw: result.distance,
    };
  }
  const ring = [...result.coords, result.coords[0]];
  return {
    annotation_type: "area",
    geometry: { type: "Polygon", coordinates: [ring] },
    measurement_value: fmtArea(result.area),
    measurement_unit: "acres",
    measurement_raw: result.area,
  };
}

export default function MeasurementSaveHUD({ measure }) {
  const session = useQcStore((s) => s.session);
  const currentPadIdx = useQcStore((s) => s.currentPadIdx);
  const saveMapAnnotation = useQcStore((s) => s.saveMapAnnotation);
  const saveInflight = useQcStore((s) => s.mapAnnotationSaveInflight);

  const result = measure?.result ?? null;
  const pad = session?.pads?.[currentPadIdx] ?? null;
  const findings = pad?.findings ?? [];

  // Build the "Save to" options, most-specific last.
  const options = [{ key: "case", label: "Case", pad_id: null, finding_id: null }];
  if (pad) {
    options.push({ key: `pad:${pad.id}`, label: `Pad: ${pad.pad_name}`, pad_id: pad.id, finding_id: null });
    for (const f of findings) {
      options.push({
        key: `finding:${f.id}`,
        label: `Finding ${f.idx + 1}${f.analysis?.feature ? ` — ${f.analysis.feature}` : ""}`,
        pad_id: pad.id,
        finding_id: f.id,
      });
    }
  }
  // Default to the most-specific reliably-known context: the current pad.
  const defaultKey = pad ? `pad:${pad.id}` : "case";

  const [targetKey, setTargetKey] = useState(defaultKey);
  const [label, setLabel] = useState("");
  const [error, setError] = useState(null);
  // Track the result identity so a NEW measurement resets the form + un-dismisses.
  const [seenResult, setSeenResult] = useState(result);
  const [dismissed, setDismissed] = useState(false);
  if (result !== seenResult) {
    setSeenResult(result);
    setDismissed(false);
    setLabel("");
    setError(null);
    setTargetKey(defaultKey);
  }

  if (!result || dismissed) return null;

  const chosen = options.find((o) => o.key === targetKey) ?? options[0];

  async function handleSave() {
    setError(null);
    const base = toPayload(result);
    const payload = {
      ...base,
      pad_id: chosen.pad_id,
      finding_id: chosen.finding_id,
      label: label.trim() || undefined,
    };
    const res = await saveMapAnnotation(payload);
    if (res.ok) {
      measure.clear?.(); // dismiss the ephemeral measurement; the saved one renders via the layer
      setDismissed(true);
    } else {
      setError(res.error || "Failed to save measurement.");
    }
  }

  return (
    <div
      className="absolute z-[80] top-[252px] left-[48px] w-[260px]
                 rounded-sm border border-amber-500/60 bg-white/95 shadow-md
                 px-2.5 py-2 text-[11px] text-slate-700"
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-semibold text-amber-700">Save measurement</span>
        <button type="button" onClick={() => setDismissed(true)}
          className="text-slate-400 hover:text-slate-700 transition" title="Dismiss (keeps it on screen, unsaved)">
          <XIcon size={13} />
        </button>
      </div>

      <label className="block text-[10px] text-fg-secondary mb-0.5">Save to</label>
      <select
        value={targetKey}
        onChange={(e) => setTargetKey(e.target.value)}
        className="w-full text-[11px] px-1.5 py-1 mb-1.5 rounded border border-slate-300
                   bg-white text-slate-700 focus:outline-none focus:border-amber-500"
      >
        {options.map((o) => (
          <option key={o.key} value={o.key}>{o.label}</option>
        ))}
      </select>

      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Label (optional) — e.g. pit footprint"
        className="w-full text-[11px] px-1.5 py-1 mb-1.5 rounded border border-slate-300
                   bg-white text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-amber-500"
      />

      {error && <div className="text-[10px] text-red-600 mb-1.5 leading-snug">{error}</div>}

      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] text-fg-secondary tabular-nums">
          {result.kind === "line" ? fmtDistance(result.distance) : fmtArea(result.area)}
        </span>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={() => setDismissed(true)} disabled={saveInflight}
            className="text-[10px] px-2 py-1 rounded border border-slate-300 text-slate-600
                       hover:bg-slate-100 disabled:opacity-40 transition">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saveInflight}
            className="flex items-center gap-1 text-[10px] px-2.5 py-1 rounded border
                       bg-amber-500/15 border-amber-500/60 text-amber-700 hover:bg-amber-500/25
                       disabled:opacity-40 disabled:cursor-not-allowed transition">
            {saveInflight ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
            {saveInflight ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
